// Paket P1 — PromoHero (60_PROMOTION.md §5).
//
// Nachthimmel-Verlaufsblock mit Sternfeld, grossem Icon in Fackelgold
// (oder Stufenfarbe), Titel, optionalem Untertitel und optionalem Ribbon
// oben rechts (z. B. "7 Tage gratis"). Wird vom Paywall-Kopf und der
// MembershipHeroCard verwendet.
//
// Animation: langsames Funkeln der Sterne (6–9 s) — unter
// `prefers-reduced-motion: reduce` ausgeschaltet (Design-System §0).

import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from '../ui';
import type { TierMarkTier } from './TierMark';
import styles from './PromoHero.module.css';

export interface PromoHeroProps {
  /** Sprite-Icon-Name (z. B. "flame", "sparkle", "crown"). Default flame. */
  icon?: IconName;
  title: string;
  subtitle?: string;
  /** Kurzer Text oben rechts, z. B. "7 Tage gratis". */
  ribbon?: string;
  /** Faerbt das grosse Icon in der Stufenfarbe statt Fackelgold. */
  tier?: TierMarkTier;
  /** Schlankere Variante (Paywall-Dialog, MembershipHeroCard). */
  compact?: boolean;
  /** Zusaetzlicher Inhalt unter dem Untertitel (z. B. CTA). */
  children?: ReactNode;
  className?: string;
  /** Optionale ID auf der Titel-<h2> (fuer aria-labelledby auf aeusseren Sektionen). */
  titleId?: string;
}

const TIER_RGB: Record<TierMarkTier, string> = {
  plus: 'var(--ff-tier-plus-rgb)',
  ultra: 'var(--ff-tier-ultra-rgb)',
  founders: 'var(--ff-tier-founders-rgb)',
  torchbearer: 'var(--ff-tier-founders-rgb)',
};

export function PromoHero({
  icon = 'flame',
  title,
  subtitle,
  ribbon,
  tier,
  compact = false,
  children,
  className,
  titleId,
}: PromoHeroProps) {
  const style = tier
    ? ({ '--icon-rgb': TIER_RGB[tier] } as CSSProperties)
    : ({ '--icon-rgb': 'var(--ff-tier-ultra-rgb)' } as CSSProperties);
  return (
    <header
      className={[styles.hero, compact ? styles.compact : '', className].filter(Boolean).join(' ')}
      style={style}
      data-compact={compact ? '1' : undefined}
      data-tier={tier ?? undefined}
    >
      <div className={styles.stars} aria-hidden="true" />
      <div className={styles.glow} aria-hidden="true" />
      <div className={styles.body}>
        <div className={styles.iconWrap} aria-hidden="true">
          <Icon name={icon} size={compact ? 36 : 56} />
        </div>
        <div className={styles.copy}>
          {ribbon ? <span className={styles.ribbon}>{ribbon}</span> : null}
          <h2 id={titleId} className={styles.title}>{title}</h2>
          {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          {children}
        </div>
      </div>
    </header>
  );
}

export default PromoHero;
